import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Clock, User, ArrowRight, Search, Filter } from 'lucide-react';
import articles from '../data/articlesComplete';
import LoadingSpinner from '../components/LoadingSpinner';
import SEO from '../components/SEO';

const Articles = () => {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [filteredArticles, setFilteredArticles] = useState(articles);

  useEffect(() => {
    // Simulate loading
    setTimeout(() => setLoading(false), 500);
  }, []);

  useEffect(() => {
    let filtered = articles;

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(article => article.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredArticles(filtered);
  }, [searchTerm, selectedCategory]);

  const categories = ['All', ...new Set(articles.map(a => a.category))];

  if (loading) {
    return <LoadingSpinner fullScreen message="Loading articles..." />;
  }

  return (
    <>
      <SEO
        title="More Information - Articles & Guides | GENMOZPRO"
        description="Comprehensive payment testing resources, guides, and articles for developers. Learn about BIN numbers, Luhn algorithm, Stripe integration, and payment security best practices."
        keywords="payment testing resources, card validation guides, bin number articles, stripe integration tutorials, payment security, luhn algorithm guide"
      />
      
      <div className="min-h-screen bg-black text-white pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center space-x-2 px-3 sm:px-4 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded-full mb-4 sm:mb-6">
              <FileText className="w-3 sm:w-4 h-3 sm:h-4 text-yellow-500" />
              <span className="text-yellow-500 text-xs sm:text-sm font-medium">Knowledge Base - 2026</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
              <span className="text-yellow-500">MORE INFORMATION</span>
            </h1>
            <p className="text-gray-400 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto">
              Expert guides, tutorials, and best practices for payment system development and testing
            </p>
          </div>

          {/* Search and Filter */}
          <div className="mb-8 flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-800 rounded-lg focus:outline-none focus:border-yellow-500 text-white transition-colors"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg focus:outline-none focus:border-yellow-500 text-white transition-colors cursor-pointer"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Results count */}
          <div className="mb-6 text-gray-400 text-sm">
            Showing {filteredArticles.length} of {articles.length} articles
          </div>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {filteredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/articles/${article.slug}`}
                className="group bg-gray-900 border border-gray-800 rounded-xl overflow-hidden hover:border-yellow-500 transition-all transform hover:scale-105"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-2">
                    <span className="px-3 py-1 bg-yellow-500/10 text-yellow-500 text-xs font-medium rounded-full">
                      {article.category}
                    </span>
                    <div className="flex items-center space-x-2 text-gray-500 text-xs">
                      <Clock className="w-3 h-3" />
                      <span>{article.readTime}</span>
                    </div>
                  </div>

                  <h2 className="text-lg sm:text-xl font-bold text-white mb-3 group-hover:text-yellow-500 transition-colors line-clamp-2">
                    {article.title}
                  </h2>

                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {article.excerpt}
                  </p>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pt-4 border-t border-gray-800 gap-2">
                    <div className="flex items-center space-x-2 text-gray-500 text-xs sm:text-sm">
                      <User className="w-3 sm:w-4 h-3 sm:h-4" />
                      <span>{article.author}</span>
                      <span>•</span>
                      <span>{new Date(article.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                    <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5 text-yellow-500 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No articles found matching your criteria</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Articles;
